import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';

// ─── Image manifest ───────────────────────────────────────────────────────────
// Replace 01.jpg–16.jpg with your own photos. Keep file names.
// orientation: 'portrait' (tall) | 'landscape' (wide)
const IMAGES = [
  { id: 1,  src: '/images/gallery/01.jpg', orientation: 'portrait'  },
  { id: 2,  src: '/images/gallery/02.jpg', orientation: 'landscape' },
  { id: 3,  src: '/images/gallery/03.jpg', orientation: 'portrait'  },
  { id: 4,  src: '/images/gallery/04.jpg', orientation: 'landscape' },
  { id: 5,  src: '/images/gallery/05.jpg', orientation: 'portrait'  },
  { id: 6,  src: '/images/gallery/06.jpg', orientation: 'landscape' },
  { id: 7,  src: '/images/gallery/07.jpg', orientation: 'portrait'  },
  { id: 8,  src: '/images/gallery/08.jpg', orientation: 'landscape' },
  { id: 9,  src: '/images/gallery/09.jpg', orientation: 'portrait'  },
  { id: 10, src: '/images/gallery/10.jpg', orientation: 'landscape' },
  { id: 11, src: '/images/gallery/11.jpg', orientation: 'portrait'  },
  { id: 12, src: '/images/gallery/12.jpg', orientation: 'landscape' },
  { id: 13, src: '/images/gallery/13.jpg', orientation: 'portrait'  },
  { id: 14, src: '/images/gallery/14.jpg', orientation: 'landscape' },
  { id: 15, src: '/images/gallery/15.jpg', orientation: 'portrait'  },
  { id: 16, src: '/images/gallery/16.jpg', orientation: 'landscape' },
];

const TOTAL = IMAGES.length;

// ─── Responsive config ───────────────────────────────────────────────────────
// Returns pixel sizes & offsets for current viewport width
const getConfig = (vw) => {
  if (vw >= 1280) return {
    center: { portrait: [360, 540], landscape: [540, 360] },
    side1:  { portrait: [155, 232], landscape: [232, 155] },
    side2:  { portrait: [108, 162], landscape: [162, 108] },
    x1: 300, x2: 470,
    containerH: 660,
    showSide2: true,
  };
  if (vw >= 768) return {
    center: { portrait: [260, 390], landscape: [390, 260] },
    side1:  { portrait: [115, 172], landscape: [172, 115] },
    side2:  { portrait: [80,  120], landscape: [120, 80]  },
    x1: 215, x2: 335,
    containerH: 490,
    showSide2: true,
  };
  return {
    center: { portrait: [185, 278], landscape: [278, 185] },
    side1:  { portrait: [85,  128], landscape: [128, 85]  },
    side2:  null,
    x1: 148, x2: 0,
    containerH: 360,
    showSide2: false,
  };
};

// ─── Scatter generator ───────────────────────────────────────────────────────
// Each card gets: random rotation, random Y-offset, tiny X-jitter
const makeScatter = (centerIdx) =>
  IMAGES.map((_, i) =>
    i === centerIdx
      ? { rot: 0, yOff: 0, xJitter: 0 }
      : {
          rot:     +(Math.random() * 40 - 20).toFixed(2),   // ±20 deg
          yOff:    +(Math.random() * 160 - 80).toFixed(1),  // ±80 px
          xJitter: +(Math.random() * 22 - 11).toFixed(1),   // ±11 px
        }
  );

// ─── Component ───────────────────────────────────────────────────────────────
const GalleryPage = () => {
  const [current,  setCurrent]  = useState(0);
  const [scatter,  setScatter]  = useState(() => makeScatter(0));
  const [playing,  setPlaying]  = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [vw,       setVw]       = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  const filmRef = useRef(null);

  // Track viewport width for responsive config
  useEffect(() => {
    const handler = () => setVw(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const cfg = useMemo(() => getConfig(vw), [vw]);

  // Navigate: pick new center, reshuffle all card scatter data
  const navigate = useCallback((rawIdx) => {
    const idx = ((rawIdx % TOTAL) + TOTAL) % TOTAL;
    setCurrent(idx);
    setScatter(makeScatter(idx));
    if (filmRef.current) {
      const thumb = filmRef.current.children[idx];
      if (thumb) thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, []);

  // Auto-play (pause on hover)
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => navigate(current + 1), 4500);
    return () => clearInterval(id);
  }, [playing, current, navigate]);

  // Pause when lightbox open
  useEffect(() => { if (lightbox !== null) setPlaying(false); }, [lightbox]);

  // Keyboard nav in lightbox
  useEffect(() => {
    const h = (e) => {
      if (lightbox === null) return;
      if (e.key === 'ArrowRight') setLightbox(p => (p + 1) % TOTAL);
      if (e.key === 'ArrowLeft')  setLightbox(p => (p - 1 + TOTAL) % TOTAL);
      if (e.key === 'Escape')     setLightbox(null);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightbox]);

  // Compute 5 visible cards: offsets -2..+2
  const visible = useMemo(() => {
    const offsets = cfg.showSide2 ? [-2, -1, 0, 1, 2] : [-1, 0, 1];
    return offsets.map(off => {
      const idx = ((current + off) % TOTAL + TOTAL) % TOTAL;
      return { idx, off, img: IMAGES[idx], sc: scatter[idx] };
    });
  }, [current, scatter, cfg.showSide2]);

  // Build inline style for each card
  const buildStyle = useCallback((off, sc, orientation) => {
    const isCenter = off === 0;
    const isS1 = Math.abs(off) === 1;
    const dimKey = isCenter ? 'center' : isS1 ? 'side1' : 'side2';
    const [w, h] = cfg[dimKey][orientation];

    if (isCenter) {
      return {
        position:   'absolute',
        left:       '50%',
        top:        '50%',
        width:      w,
        height:     h,
        marginLeft: -(w / 2),
        marginTop:  -(h / 2),
        transform:  'none',
        zIndex:     10,
        opacity:    1,
        cursor:     'zoom-in',
        transition: 'all 0.72s cubic-bezier(0.22, 1.5, 0.36, 1)',
        boxShadow:  '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1.5px rgba(197,160,89,0.35)',
      };
    }

    const dir   = off < 0 ? -1 : 1;
    const baseX = dir * (isS1 ? cfg.x1 : cfg.x2);

    return {
      position:   'absolute',
      left:       '50%',
      top:        '50%',
      width:      w,
      height:     h,
      marginLeft: -(w / 2),
      marginTop:  -(h / 2),
      transform:  `translateX(${baseX + sc.xJitter}px) translateY(${sc.yOff}px) rotate(${sc.rot}deg)`,
      zIndex:     isS1 ? 5 : 3,
      opacity:    isS1 ? 0.82 : 0.52,
      cursor:     'pointer',
      transition: 'all 0.72s cubic-bezier(0.22, 1.5, 0.36, 1)',
    };
  }, [cfg]);

  return (
    <div className="min-h-screen bg-onyx">
      <Helmet>
        <title>Gallery - Luxe Me Japan | Event Gallery 2026 Kuala Lumpur</title>
        <meta name="description" content="Exclusive event gallery from the Luxe Me Japan Press & B2B Day, Early 2026 at Hotel St. Regis, Kuala Lumpur." />
      </Helmet>

      {/* ── Hero Header ──────────────────────────────────────────────────── */}
      <header className="relative pt-36 pb-16 sm:pt-44 sm:pb-20 px-4 sm:px-6 lg:px-12 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-gold/8 blur-[130px] rounded-full"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-4 mb-6 sm:mb-8 opacity-0 animate-fade-up">
            <span className="w-10 h-px bg-gold/60"></span>
            <span className="text-gold tracking-[0.45em] text-[10px] sm:text-xs uppercase font-light">Luxe Me Japan</span>
            <span className="w-10 h-px bg-gold/60"></span>
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl mb-6 sm:mb-8 leading-tight opacity-0 animate-fade-up stagger-1">
            <span className="text-gradient">Event</span>
            <span className="text-platinum"> Gallery</span>
          </h1>
          <div className="opacity-0 animate-fade-up stagger-2">
            <p className="text-platinum/60 font-light text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-4">
              An intimate gathering of beauty, culture and excellence — captured in moments.
            </p>
            <p className="font-serif text-gold italic text-lg sm:text-xl">
              Early 2026 &nbsp;·&nbsp; Hotel St. Regis, Kuala Lumpur
            </p>
          </div>
          <div className="mt-8 sm:mt-12 flex items-center justify-center gap-4 opacity-0 animate-fade-up stagger-3">
            <span className="w-24 sm:w-40 h-px bg-gradient-to-r from-transparent to-gold/40"></span>
            <span className="w-1.5 h-1.5 rounded-full bg-gold"></span>
            <span className="w-24 sm:w-40 h-px bg-gradient-to-l from-transparent to-gold/40"></span>
          </div>
        </div>
      </header>

      {/* ── Playing-Card Stage ───────────────────────────────────────────── */}
      <section
        className="relative select-none"
        style={{ paddingTop: 20, paddingBottom: 60 }}
        onMouseEnter={() => setPlaying(false)}
        onMouseLeave={() => setPlaying(true)}
        data-testid="gallery-carousel"
      >
        {/* Dark velvet table background */}
        <div className="absolute inset-0 bg-gradient-to-b from-onyx via-ebony/60 to-onyx pointer-events-none" />

        {/* Card stage */}
        <div
          className="relative mx-auto overflow-visible"
          style={{ height: cfg.containerH, maxWidth: 1200 }}
        >
          {visible.map(({ idx, off, img, sc }) => {
            const isCenter = off === 0;
            const style = buildStyle(off, sc, img.orientation);

            return (
              <div
                key={idx}
                style={style}
                onClick={() => {
                  if (isCenter) setLightbox(idx);
                  else navigate(current + off);
                }}
              >
                {/* Card body */}
                <div
                  className={`w-full h-full rounded-xl overflow-hidden ${
                    isCenter
                      ? 'border-2 border-gold/40'
                      : 'border border-white/15'
                  }`}
                >
                  <img
                    src={img.src}
                    alt={`Gallery ${idx + 1}`}
                    className={`w-full h-full object-cover transition-all duration-700 ${
                      isCenter ? 'brightness-100' : 'brightness-65 saturate-75'
                    }`}
                    draggable={false}
                  />

                  {/* Center: zoom hint on hover */}
                  {isCenter && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/25 transition-colors duration-300 group">
                      <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-70 drop-shadow-xl transition-opacity duration-300" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Prev / Next Arrows ── */}
        <div className="relative z-20" style={{ marginTop: -cfg.containerH / 2 - 28, height: 56 }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-8 flex justify-between">
            <button
              data-testid="gallery-prev"
              onClick={() => navigate(current - 1)}
              className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-onyx/80 backdrop-blur-sm border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-onyx transition-all duration-300 hover:scale-110 group"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <button
              data-testid="gallery-next"
              onClick={() => navigate(current + 1)}
              className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-onyx/80 backdrop-blur-sm border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-onyx transition-all duration-300 hover:scale-110 group"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>

        {/* Image counter */}
        <p className="text-center text-platinum/30 text-xs tracking-[0.35em] uppercase mt-8 relative z-10">
          <span className="text-gold font-medium">{String(current + 1).padStart(2, '0')}</span>
          {' / '}
          {String(TOTAL).padStart(2, '0')}
        </p>
      </section>

      {/* ── Filmstrip ────────────────────────────────────────────────────── */}
      <section className="py-6 sm:py-10 px-4 sm:px-10 lg:px-16" data-testid="gallery-filmstrip">
        <p className="text-center text-platinum/25 text-[10px] tracking-[0.45em] uppercase mb-5">
          All photographs
        </p>
        <div
          ref={filmRef}
          className="flex gap-2 sm:gap-3 overflow-x-auto py-2 px-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {IMAGES.map((img, idx) => {
            const isActive = idx === current;
            const isLand   = img.orientation === 'landscape';
            return (
              <button
                key={img.id}
                data-testid={`gallery-thumb-${idx}`}
                onClick={() => navigate(idx)}
                style={{ width: isLand ? 76 : 50, height: isLand ? 50 : 76, flexShrink: 0 }}
                className={`rounded-md overflow-hidden transition-all duration-300 ${
                  isActive
                    ? 'ring-2 ring-gold ring-offset-2 ring-offset-onyx opacity-100 scale-110 shadow-lg shadow-gold/20'
                    : 'opacity-40 hover:opacity-75 hover:scale-105'
                }`}
                aria-label={`Photo ${idx + 1}`}
              >
                <img src={img.src} alt="" className="w-full h-full object-cover" draggable={false} />
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Lightbox ─────────────────────────────────────────────────────── */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-onyx/97 backdrop-blur-2xl flex items-center justify-center p-4 sm:p-8"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <button
            className="absolute top-5 right-5 sm:top-8 sm:right-8 z-10 bg-onyx/60 rounded-full p-2 text-platinum/60 hover:text-gold transition-colors"
            onClick={() => setLightbox(null)}
          >
            <X className="w-7 h-7" />
          </button>
          <button
            className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-onyx/70 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-onyx transition-all duration-300"
            onClick={(e) => { e.stopPropagation(); setLightbox(p => (p - 1 + TOTAL) % TOTAL); }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 z-10 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-onyx/70 border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-onyx transition-all duration-300"
            onClick={(e) => { e.stopPropagation(); setLightbox(p => (p + 1) % TOTAL); }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div
            className="relative max-h-[85vh] flex items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={IMAGES[lightbox].src}
              alt={`Gallery ${lightbox + 1}`}
              className="max-h-[82vh] max-w-[90vw] rounded-xl shadow-2xl object-contain border border-white/10"
            />
            <p className="absolute -bottom-8 left-0 right-0 text-center text-platinum/35 text-xs tracking-widest">
              {String(lightbox + 1).padStart(2, '0')} / {String(TOTAL).padStart(2, '0')}
            </p>
          </div>
        </div>
      )}

      {/* ── Footer accent ────────────────────────────────────────────────── */}
      <div className="py-16 sm:py-20 text-center px-4">
        <div className="w-24 h-px bg-gold/20 mx-auto mb-6"></div>
        <p className="text-platinum/25 text-xs tracking-[0.45em] uppercase font-light">
          Photographs from the Luxe Me Japan event
        </p>
        <p className="font-serif text-platinum/20 italic text-sm mt-2">
          Hotel St. Regis · Kuala Lumpur · 2026
        </p>
      </div>
    </div>
  );
};

export default GalleryPage;
